import { useEffect, useState, useCallback } from 'react';

export interface UseDelayedAnimationOptions {
    delay?: number;
    onStateChange?: (animated: boolean) => void;
}

export interface UseDelayedAnimationReturn {
    animated: boolean;
    reset: () => void;
    forceAnimate: () => void;
}

export function useDelayedAnimation(
    condition: boolean,
    options: UseDelayedAnimationOptions = {}
): UseDelayedAnimationReturn {
    const { delay = 0, onStateChange } = options;
    const [animated, setAnimated] = useState(false);

    const notifyStateChange = useCallback((state: boolean) => {
        onStateChange?.(state);
    }, [onStateChange]);

    const reset = useCallback(() => {
        setAnimated(false);
        notifyStateChange(false);
    }, [notifyStateChange]);

    const forceAnimate = useCallback(() => {
        setAnimated(true);
        notifyStateChange(true);
    }, [notifyStateChange]);

    useEffect(() => {
        if (!condition) {
            reset();
            return;
        }

        const timerId = setTimeout(() => {
            setAnimated(true);
            notifyStateChange(true);
        }, delay);

        return () => {
            clearTimeout(timerId);
        };
    }, [condition, delay, reset, notifyStateChange]);

    return {
        animated,
        reset,
        forceAnimate,
    };
}